import {
  Box,
  Text,
  HStack,
  VStack,
  Badge,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuContent,
  MenuItem,
  MenuPositioner,
} from '@chakra-ui/react'
import { Download, Eye, Trash2, User, FolderOpen, Folder, MoreVertical, Pencil } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { DocumentWithRelations } from '../types'
import { useFolderPath } from '../hooks/useFolders'
import { renderFileIcon } from '../config/fileIcons'

interface DocumentCardProps {
  document: DocumentWithRelations
  showResidenceInfo?: boolean
  draggable?: boolean
  isActive?: boolean
  onView?: (document: DocumentWithRelations) => void
  onEdit?: (document: DocumentWithRelations) => void
  onDownload?: (document: DocumentWithRelations) => void
  onDelete?: (document: DocumentWithRelations) => void
}

// Fonction pour formater la taille du fichier
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentCard({
  document,
  showResidenceInfo = false,
  draggable = false,
  isActive = false,
  onView,
  onEdit,
  onDownload,
  onDelete,
}: DocumentCardProps) {
  const { t } = useTranslation()
  const { data: folderPath } = useFolderPath(document.folder_id || undefined)

  const handleDragStart = (e: React.DragEvent) => {
    if (!draggable) return

    // Set the document ID in the dataTransfer
    e.dataTransfer.setData('application/document-id', document.id)
    e.dataTransfer.setData('text/plain', document.id) // Fallback for compatibility
    e.dataTransfer.effectAllowed = 'move'

    console.log('🎯 Drag started for document:', document.id)

    // Add a visual effect
    const target = e.currentTarget as HTMLElement
    target.style.opacity = '0.5'
  }

  const handleDragEnd = (e: React.DragEvent) => {
    // Reset visual effect
    const target = e.currentTarget as HTMLElement
    target.style.opacity = '1'
  }

  return (
    <Box
      p={4}
      borderWidth="1px"
      borderRadius="md"
      bg={isActive ? 'brand.50' : 'white'}
      borderColor={isActive ? 'brand.300' : 'gray.200'}
      _hover={{
        bg: isActive ? 'brand.100' : 'gray.50',
        borderColor: 'brand.300',
        shadow: 'sm',
      }}
      transition="all 0.2s"
      cursor={draggable ? 'grab' : 'pointer'}
      onClick={() => onView?.(document)}
      draggable={draggable}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      data-document-card
    >
      <HStack gap={4} align="start">
        {/* File Icon */}
        <Box
          p={3}
          borderRadius="md"
          bg="gray.50"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          {renderFileIcon(document.mime_type, document.original_filename, 32)}
        </Box>

        {/* Document Info */}
        <VStack flex={1} align="start" gap={2}>
          {/* Title, Category and Actions */}
          <HStack gap={2} width="full" justify="space-between" align="center">
            <Text fontWeight="semibold" fontSize="md" lineClamp={1} flex={1}>
              {document.display_name}
            </Text>
            <HStack gap={2} flexShrink={0}>
              <Badge colorPalette="blue" size="sm">
                {t(`documents.categories.${document.category}`)}
              </Badge>
              <Box onClick={(e) => e.stopPropagation()}>
                <MenuRoot positioning={{ placement: 'bottom-end' }}>
                  <MenuTrigger asChild>
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={t('common.actions')}
                    >
                      <MoreVertical size={18} />
                    </IconButton>
                  </MenuTrigger>
                  <MenuPositioner>
                    <MenuContent minW="160px">
                      {onView && (
                        <MenuItem value="view" onClick={() => onView(document)}>
                          <HStack gap={2}>
                            <Eye size={16} />
                            <Text>{t('common.view')}</Text>
                          </HStack>
                        </MenuItem>
                      )}
                      {onEdit && (
                        <MenuItem value="edit" onClick={() => onEdit(document)}>
                          <HStack gap={2}>
                            <Pencil size={16} />
                            <Text>{t('common.edit')}</Text>
                          </HStack>
                        </MenuItem>
                      )}
                      {onDownload && (
                        <MenuItem value="download" onClick={() => onDownload(document)}>
                          <HStack gap={2}>
                            <Download size={16} />
                            <Text>{t('common.download')}</Text>
                          </HStack>
                        </MenuItem>
                      )}
                      {onDelete && (
                        <MenuItem
                          value="delete"
                          onClick={() => onDelete(document)}
                          color="red.600"
                          _hover={{ bg: 'red.50' }}
                        >
                          <HStack gap={2}>
                            <Trash2 size={16} />
                            <Text>{t('common.delete')}</Text>
                          </HStack>
                        </MenuItem>
                      )}
                    </MenuContent>
                  </MenuPositioner>
                </MenuRoot>
              </Box>
            </HStack>
          </HStack>

          {/* Metadata Row 1: File info */}
          <HStack gap={3} fontSize="xs" color="gray.600" wrap="wrap">
            <Text>{document.original_filename}</Text>
            <Text>•</Text>
            <Text>{formatFileSize(document.file_size)}</Text>
            <Text>•</Text>
            <Text>
              {new Date(document.created_at).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </Text>
          </HStack>

          {/* Metadata Row 2: Creator info */}
          {document.uploader && (
            <HStack gap={2} fontSize="xs" color="gray.600">
              <User size={12} />
              <Text>
                Créé par{' '}
                <Text as="span" fontWeight="medium">
                  {document.uploader.first_name} {document.uploader.last_name}
                </Text>
              </Text>
            </HStack>
          )}

          {/* Metadata Row 3: Folder path (when showing residence info) */}
          {showResidenceInfo && (
            <VStack align="start" gap={1} fontSize="xs" color="gray.500">
              {document.residence && (
                <HStack gap={2}>
                  <FolderOpen size={12} />
                  <Text>
                    {document.residence.residence_key
                      ? `${document.residence.residence_key} - ${document.residence.name}`
                      : document.residence.name}
                  </Text>
                </HStack>
              )}
              {folderPath && (
                <HStack gap={2}>
                  <Folder size={12} />
                  <Text lineClamp={1}>{folderPath}</Text>
                </HStack>
              )}
            </VStack>
          )}

          {/* Document type if available */}
          {document.document_type && (
            <Badge colorPalette="gray" size="sm">
              {document.document_type.replace(/_/g, ' ')}
            </Badge>
          )}
        </VStack>
      </HStack>
    </Box>
  )
}
